/**
 * OSINTIntelPanel — Shodan & VirusTotal threat intelligence lookup
 * Inspired by pcybox-attack integrations
 */
import React, { useState } from 'react';
import {
  Search, Globe, Shield, AlertTriangle, CheckCircle, XCircle,
  Loader2, ExternalLink, Server, Wifi, Lock, Eye, MapPin, Building,
  AlertOctagon, Info, ChevronDown, ChevronUp, Zap
} from 'lucide-react';
import { toast } from '../utils/toastUtils';

const API_BASE = '/api/v1/osint';

const RISK_COLORS = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'green',
};

const RiskBadge = ({ level }) => {
  const color = RISK_COLORS[level] || 'gray';
  const labels = { critical: 'Critique', high: 'Haut', medium: 'Moyen', low: 'Faible' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${color}-900/30 text-${color}-400 border border-${color}-500/30`}>
      {labels[level] || level}
    </span>
  );
};

// ============================================================
// SHODAN PANEL
// ============================================================
const ShodanResult = ({ data }) => {
  const [showServices, setShowServices] = useState(false);

  if (data.error) {
    return (
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
        <p className="text-yellow-400 text-sm font-medium">{data.error}</p>
        {data.hint && <p className="text-gray-400 text-xs mt-1">{data.hint}</p>}
      </div>
    );
  }

  if (!data.found) {
    return (
      <div className="bg-gray-800/50 border border-gray-600/30 rounded-lg p-4">
        <p className="text-gray-400 text-sm">{data.message || 'Host not found in Shodan'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* General info */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: <Globe className="w-4 h-4 text-blue-400" />, label: 'IP', value: data.ip },
          { icon: <Building className="w-4 h-4 text-purple-400" />, label: 'Organisation', value: data.org },
          { icon: <Wifi className="w-4 h-4 text-cyan-400" />, label: 'ISP', value: data.isp },
          { icon: <MapPin className="w-4 h-4 text-green-400" />, label: 'Localisation', value: data.city ? `${data.city}, ${data.country}` : data.country },
          { icon: <Server className="w-4 h-4 text-orange-400" />, label: 'ASN', value: data.asn },
          { icon: <Eye className="w-4 h-4 text-pink-400" />, label: 'OS', value: data.os || 'Inconnu' },
        ].map((item, i) => (
          <div key={i} className="bg-brand-black-light rounded-lg p-3 border border-gray-700/50">
            <div className="flex items-center gap-2 mb-1">
              {item.icon}
              <span className="text-xs text-gray-500">{item.label}</span>
            </div>
            <p className="text-sm text-white font-medium truncate">{item.value || '—'}</p>
          </div>
        ))}
      </div>

      {/* Ports ouverts */}
      <div className="bg-brand-black-light rounded-lg p-4 border border-gray-700/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-brand-green" />
            <span className="text-sm font-medium text-white">Ports Ouverts ({data.ports_count})</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {(data.open_ports || []).slice(0, 30).map((port) => (
            <span key={port} className="px-2 py-1 bg-brand-green/10 border border-brand-green/30 rounded text-xs text-brand-green font-mono">
              {port}
            </span>
          ))}
          {data.ports_count > 30 && (
            <span className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">+{data.ports_count - 30} autres</span>
          )}
        </div>
      </div>

      {/* Vulnerabilities */}
      {data.vuln_count > 0 && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">{data.vuln_count} CVE detected</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.vulnerabilities.slice(0, 20).map((cve) => (
              <a
                key={cve}
                href={`https://nvd.nist.gov/vuln/detail/${cve}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2 py-1 bg-red-900/30 border border-red-500/40 rounded text-xs text-red-400 hover:text-red-300 font-mono flex items-center gap-1"
              >
                {cve}
                <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Detected services */}
      {data.services?.length > 0 && (
        <div className="bg-brand-black-light rounded-lg border border-gray-700/50 overflow-hidden">
          <button
            onClick={() => setShowServices(!showServices)}
            className="w-full p-3 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-white">Detected services ({data.services.length})</span>
            </div>
            {showServices ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {showServices && (
            <div className="divide-y divide-gray-800">
              {data.services.slice(0, 15).map((svc, i) => (
                <div key={i} className="px-4 py-2 flex items-start gap-3">
                  <span className="text-brand-green font-mono text-xs pt-0.5 min-w-[3rem]">{svc.port}/{svc.protocol}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-medium">{svc.service}</span>
                      {svc.product && <span className="text-gray-400 text-xs">{svc.product} {svc.version}</span>}
                    </div>
                    {svc.banner && <p className="text-gray-500 text-xs mt-0.5 truncate">{svc.banner}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {data.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {data.tags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-blue-900/20 border border-blue-500/30 rounded text-xs text-blue-400">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// VIRUSTOTAL PANEL
// ============================================================
const VirusTotalResult = ({ data }) => {
  const [showEngines, setShowEngines] = useState(false);

  if (data.error) {
    return (
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
        <p className="text-yellow-400 text-sm font-medium">{data.error}</p>
        {data.hint && <p className="text-gray-400 text-xs mt-1">{data.hint}</p>}
      </div>
    );
  }

  if (!data.found) {
    return (
      <div className="bg-gray-800/50 border border-gray-600/30 rounded-lg p-4">
        <p className="text-gray-400 text-sm">{data.message || 'Target not found in VirusTotal'}</p>
      </div>
    );
  }

  const stats = data.analysis_stats || {};
  const flaggedEntries = Object.entries(data.flagged_engines || {});
  const riskColor = RISK_COLORS[data.risk_level] || 'gray';

  return (
    <div className="space-y-4">
      {/* Score global */}
      <div className={`bg-${riskColor}-900/20 border border-${riskColor}-500/30 rounded-lg p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {data.risk_level === 'critical' || data.risk_level === 'high' ? (
              <AlertOctagon className={`w-8 h-8 text-${riskColor}-400`} />
            ) : (
              <Shield className={`w-8 h-8 text-${riskColor}-400`} />
            )}
            <div>
              <p className={`text-lg font-bold text-${riskColor}-400`}>
                {data.malicious_count}/{data.total_engines} engines detect a threat
              </p>
              <p className="text-gray-400 text-xs">Reputation: {data.reputation}</p>
            </div>
          </div>
          <RiskBadge level={data.risk_level} />
        </div>
      </div>

      {/* Detailed stats */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Malveillant', value: stats.malicious || 0, color: 'red' },
          { label: 'Suspect', value: stats.suspicious || 0, color: 'orange' },
          { label: 'Not detected', value: stats.undetected || 0, color: 'gray' },
          { label: 'Propre', value: stats.harmless || 0, color: 'green' },
        ].map((s) => (
          <div key={s.label} className={`bg-${s.color}-900/20 border border-${s.color}-500/30 rounded-lg p-3 text-center`}>
            <p className={`text-2xl font-bold text-${s.color}-400`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Network info */}
      {(data.country || data.asn || data.as_owner) && (
        <div className="grid grid-cols-3 gap-3">
          {data.country && (
            <div className="bg-brand-black-light rounded-lg p-3 border border-gray-700/50">
              <p className="text-xs text-gray-500 mb-1">Pays</p>
              <p className="text-sm text-white font-medium">{data.country}</p>
            </div>
          )}
          {data.asn && (
            <div className="bg-brand-black-light rounded-lg p-3 border border-gray-700/50">
              <p className="text-xs text-gray-500 mb-1">ASN</p>
              <p className="text-sm text-white font-mono">{data.asn}</p>
            </div>
          )}
          {data.as_owner && (
            <div className="bg-brand-black-light rounded-lg p-3 border border-gray-700/50">
              <p className="text-xs text-gray-500 mb-1">AS Owner</p>
              <p className="text-sm text-white font-medium truncate">{data.as_owner}</p>
            </div>
          )}
        </div>
      )}

      {/* Categories */}
      {Object.keys(data.categories || {}).length > 0 && (
        <div className="bg-brand-black-light rounded-lg p-3 border border-gray-700/50">
          <p className="text-xs text-gray-500 mb-2">Categories</p>
          <div className="flex flex-wrap gap-2">
            {Object.values(data.categories).slice(0, 8).map((cat, i) => (
              <span key={i} className="px-2 py-0.5 bg-blue-900/20 border border-blue-500/30 rounded text-xs text-blue-400">{cat}</span>
            ))}
          </div>
        </div>
      )}

      {/* Engines that detected */}
      {flaggedEntries.length > 0 && (
        <div className="bg-brand-black-light rounded-lg border border-gray-700/50 overflow-hidden">
          <button
            onClick={() => setShowEngines(!showEngines)}
            className="w-full p-3 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-medium text-white">Engines that detected ({flaggedEntries.length})</span>
            </div>
            {showEngines ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {showEngines && (
            <div className="divide-y divide-gray-800">
              {flaggedEntries.map(([engine, result]) => (
                <div key={engine} className="px-4 py-2 flex items-center justify-between">
                  <span className="text-white text-xs font-medium">{engine}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">{result.result}</span>
                    <RiskBadge level={result.category === 'malicious' ? 'high' : 'medium'} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const OSINTIntelPanel = ({ defaultTarget = '' }) => {
  const [target, setTarget] = useState(defaultTarget);
  const [activeTab, setActiveTab] = useState('shodan');
  const [loading, setLoading] = useState({ shodan: false, virustotal: false });
  const [results, setResults] = useState({ shodan: null, virustotal: null });

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
  };

  const lookupShodan = async () => {
    if (!target.trim()) { toast.error('Entrez une cible (IP ou domaine)'); return; }
    setLoading(prev => ({ ...prev, shodan: true }));
    try {
      const res = await fetch(`${API_BASE}/shodan/host`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ target: target.trim() })
      });
      const data = await res.json();
      setResults(prev => ({ ...prev, shodan: data }));
      setActiveTab('shodan');
      toast.success('Shodan lookup completed');
    } catch (e) {
      toast.error(`Erreur Shodan: ${e.message}`);
    } finally {
      setLoading(prev => ({ ...prev, shodan: false }));
    }
  };

  const lookupVT = async () => {
    if (!target.trim()) { toast.error('Entrez une cible (IP ou domaine)'); return; }
    setLoading(prev => ({ ...prev, virustotal: true }));
    try {
      const res = await fetch(`${API_BASE}/virustotal/analyze`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ target: target.trim(), target_type: 'auto' })
      });
      const data = await res.json();
      setResults(prev => ({ ...prev, virustotal: data }));
      setActiveTab('virustotal');
      toast.success('VirusTotal analysis completed');
    } catch (e) {
      toast.error(`Erreur VirusTotal: ${e.message}`);
    } finally {
      setLoading(prev => ({ ...prev, virustotal: false }));
    }
  };

  const isLoading = loading.shodan || loading.virustotal;

  return (
    <div className="bg-brand-black-light border border-gray-700/50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-purple-900/20 to-blue-900/20">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-purple-400" />
          <h3 className="text-base font-semibold text-white">Threat Intelligence</h3>
          <span className="text-xs text-gray-500 ml-auto">Shodan · VirusTotal</span>
        </div>
        {/* Search bar */}
        <div className="flex gap-2">
          <input
            type="text"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && lookupShodan()}
            placeholder="IP, domaine ou URL..."
            className="flex-1 bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/60"
          />
          <button
            onClick={lookupShodan}
            disabled={loading.shodan || !target.trim()}
            className="px-3 py-2 bg-blue-600/80 hover:bg-blue-600 disabled:opacity-50 rounded-lg text-xs font-medium text-white flex items-center gap-1 transition-colors"
            title="Lookup Shodan"
          >
            {loading.shodan ? <Loader2 className="w-3 h-3 animate-spin" /> : <Server className="w-3 h-3" />}
            Shodan
          </button>
          <button
            onClick={lookupVT}
            disabled={loading.virustotal || !target.trim()}
            className="px-3 py-2 bg-purple-600/80 hover:bg-purple-600 disabled:opacity-50 rounded-lg text-xs font-medium text-white flex items-center gap-1 transition-colors"
            title="Analyse VirusTotal"
          >
            {loading.virustotal ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
            VirusTotal
          </button>
        </div>
      </div>

      {/* Tabs */}
      {(results.shodan || results.virustotal) && (
        <div className="flex border-b border-gray-800">
          {results.shodan && (
            <button
              onClick={() => setActiveTab('shodan')}
              className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'shodan'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-900/10'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <Server className="w-3.5 h-3.5" /> Shodan
            </button>
          )}
          {results.virustotal && (
            <button
              onClick={() => setActiveTab('virustotal')}
              className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'virustotal'
                  ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-900/10'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <Shield className="w-3.5 h-3.5" /> VirusTotal
            </button>
          )}
        </div>
      )}

      {/* Results */}
      <div className="p-4">
        {!results.shodan && !results.virustotal && (
          <div className="text-center py-8 text-gray-500">
            <Globe className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Entrez une IP ou un domaine pour lancer une analyse threat intelligence</p>
            <p className="text-xs mt-1 opacity-60">Shodan: internet exposure · VirusTotal: reputation & malware</p>
          </div>
        )}

        {activeTab === 'shodan' && results.shodan && (
          <ShodanResult data={results.shodan} />
        )}
        {activeTab === 'virustotal' && results.virustotal && (
          <VirusTotalResult data={results.virustotal} />
        )}
      </div>
    </div>
  );
};

export default OSINTIntelPanel;
